import { UIElement } from '../ui-element';
import { ElementConfig } from '../ui-element.type';
import { Attribute } from '../../decorators/attribute.decorator';
import { CmsAttribute } from '../../decorators/cms-attribute.decorator';
import { Component } from '../../decorators/component.decorator';
import { CMS_VALUE_TYPES, CMS_CONTENT_MODES } from '../../cms';
import styles from './ui-journey-item.css';
import { runInNextFrame } from '../../global/helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIJourneyItem
 * @element ui-journey-item
 * @classdesc Single step item in a `ui-journey`. Renders optional image, step number rail, and text.
 * If any of the child items in the parent `ui-journey` is missing an image URL, images are not shown.
 * @property {string} [img=''] {@attr img} URL of the image.
 * @property {string} [heading=''] {@attr heading} Heading of the step.
 * @property {string} [description=''] {@attr description} Description of the step.
 * @property {string} [linkHref=''] {@attr link-href} URL for the optional link shown below the description.
 * @property {string} [linkLabel=''] {@attr link-label} Label text for the optional link shown below the description.
 * @public
 * @slot
 * @example
 * <ui-journey>
 *   <ui-journey-item img="assets/mocks/images/journey-mock-01.png" heading="First step" description="Do the initial setup" link-href="/step-1" link-label="Learn more">
 *   </ui-journey-item>
 *   <ui-journey-item img="assets/mocks/images/journey-mock-02.png" heading="Second step" description="Do the 2nd step">
 *   </ui-journey-item>
 *   <ui-journey-item img="assets/mocks/images/journey-mock-03.png" heading="Final step" description="Do the final step">
 *   </ui-journey-item>
 * </ui-journey>
 */

@Component({
    tag: 'ui-journey-item',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
    },
})
export class UIJourneyItem extends UIElement {
    @CmsAttribute({ valueType: CMS_VALUE_TYPES.Url })
    @Attribute(String)
    img = '';
    @Attribute(String) heading = '';
    @Attribute(String) description = '';
    @CmsAttribute({ valueType: CMS_VALUE_TYPES.Url })
    @Attribute(String)
    linkHref = '';
    @Attribute(String) linkLabel = '';

    private resizeObserver?: ResizeObserver;
    private handleResizeRailAndLoad = () => this.updateStepRailsHeight();

    /**
     * Creates the image wrapper element for the journey item.
     * Renders a div containing an img element with the provided image URL and heading as alt text.
     * @returns {ElementConfig}
     * @private
     */
    private createImageWrapper(): ElementConfig {
        return {
            tagName: 'div',
            classList: { 'ui-journey__image-wrapper': true },
            children: [
                this.img && {
                    tagName: 'img',
                    attributes: {
                        src: this.img,
                        alt: this.heading || 'Journey image',
                        loading: 'lazy',
                    },
                },
            ],
        };
    }

    /**
     * Creates the content section element for the journey item.
     * Includes the rail section and text section as children.
     * @returns {ElementConfig}
     * @private
     */
    private createContentSection(): ElementConfig {
        return {
            tagName: 'div',
            classList: { 'ui-journey__content': true },
            children: [this.createRailSection(), this.createTextSection()],
        };
    }

    /**
     * Creates the rail section element for the journey item.
     * Renders a div containing the step number and a divider.
     * @returns {ElementConfig}
     * @private
     */
    private createRailSection(): ElementConfig {
        return {
            tagName: 'div',
            classList: { 'ui-journey__rail': true },
            children: [
                {
                    tagName: 'span',
                    classList: { 'ui-journey__number': true },
                },
                {
                    tagName: 'div',
                    classList: { 'ui-journey__divider': true },
                },
            ],
        };
    }

    /**
     * Creates the text section element for the journey item.
     * Renders a div containing the heading and description.
     * @returns {ElementConfig}
     * @private
     */
    private createTextSection(): ElementConfig {
        return {
            tagName: 'div',
            classList: { 'ui-journey__text-wrapper': true },
            children: [
                {
                    tagName: 'h3',
                    classList: { 'ui-journey__heading': true },
                    children: [this.heading],
                },
                {
                    tagName: 'p',
                    classList: { 'ui-journey__description': true },
                    children: [this.description],
                },
                this.linkHref && {
                    tagName: 'ui-link',
                    classList: { 'ui-journey__link': true },
                    attributes: {
                        layout: 'primary',
                        href: this.linkHref,
                        label: this.linkLabel || this.linkHref,
                    },
                },
            ],
        };
    }

    /**
     * Indicates whether images should be shown for this journey item.
     * Determined by the `show-images` attribute on the closest `ui-journey` parent.
     * @returns {boolean} True if images should be shown, false otherwise.
     * @readonly
     * @private
     */
    private get showImages(): boolean {
        const value = this.closest('ui-journey')?.getAttribute('show-images');
        return value !== null && value !== 'false';
    }

    /**
     * Updates the height of the rail divider between journey item numbers.
     * Calculates the vertical distance between the current and next step number,
     * and sets the CSS variable `--rail-after-height` for each rail element.
     * Ensures the divider visually connects steps in the journey.
     * For tablet and mobile views only.
     * @private
     */
    private updateStepRailsHeight(): void {
        const items = Array.from(
            document.querySelectorAll('.ui-journey__item')
        );
        items.forEach((item, i) => {
            const rail = item.querySelector<HTMLElement>('.ui-journey__rail');
            const stepNumber = rail?.querySelector<HTMLElement>(
                '.ui-journey__number'
            );
            if (!rail || !stepNumber) {
                return;
            }

            const isLastItem = i === items.length - 1;
            if (isLastItem) {
                rail.style.setProperty('--rail-after-height', '0');
                return;
            }

            const nextStepNumber = items[i + 1].querySelector<HTMLElement>(
                '.ui-journey__number'
            );
            if (!nextStepNumber) {
                return;
            }

            // Get bounding rectangle position for current and next step numbers
            const currentStepRect = stepNumber.getBoundingClientRect();
            const nextStepRect = nextStepNumber.getBoundingClientRect();
            // And calculate the vertical gap between them
            const verticalGap =
                nextStepRect.top -
                (currentStepRect.top + currentStepRect.height);
            const railDividerHeight = Math.max(0, Math.round(verticalGap));

            rail.style.setProperty(
                '--rail-after-height',
                `${railDividerHeight}px`
            );
        });
    }

    /**
     * Initializes automatic height adjustment for the step rail divider.
     * Sets up a ResizeObserver to monitor journey items and updates rail heights on load and custom resize events.
     * For tablet and mobile views only.
     * @private
     */
    private initStepRailAutoHeight(): void {
        this.resizeObserver = new ResizeObserver(() =>
            this.updateStepRailsHeight()
        );
        document
            .querySelectorAll('.ui-journey__item')
            .forEach((el) => this.resizeObserver?.observe(el));

        window.addEventListener('load', this.handleResizeRailAndLoad);
        window.addEventListener('resize-rail', this.handleResizeRailAndLoad);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.setAttribute('role', 'listitem');
        const children: Array<ElementConfig> = [];

        if (this.showImages && this.img) {
            children.push(this.createImageWrapper());
        }

        children.push(this.createContentSection());
        this.updateElement({
            classList: { 'ui-journey__item': true },
            children,
        });
        runInNextFrame(() => this.updateStepRailsHeight());
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.initStepRailAutoHeight();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.resizeObserver?.disconnect();
        window.removeEventListener('load', this.handleResizeRailAndLoad);
        window.removeEventListener('resize-rail', this.handleResizeRailAndLoad);
    }
}
